import {PUBLIC_METAL_SOURCES} from './publicMetalQuotes';

export const BUDGET_REFERENCE_SOURCE = {id: 'mokaab-public-usd', label: 'دلار آزاد؛ تابلوی قیمت مکعب', url: 'https://mokaab.gold/pages/prices', dataUrl: 'https://mokaab.gold/api/price-board'};
// Editorial planning budgets, NOT observed product prices. The FX anchor is a
// captured public quote; keeping it fixed makes future revisions reproducible.
export const BUDGET_BASELINE = {usdToman: 259700, publishedAt: '2026-10-01T15:15:00.000Z', version: 'planning-budgets-2026-10-01'};
const metalWeights = {jewelry: ['gold_18k', 12], gold: ['gold_18k', 5], silver: ['silver', 100], copper: ['copper', 1000]};
const validAmount = value => Number.isSafeInteger(value) && value > 0 && value <= 10000000;
function zonedTime(value) {
  if (typeof value !== 'string') return false;
  const parts = /^(\d{4})-(\d{2})-(\d{2})T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.exec(value);
  if (!parts || !Number.isFinite(Date.parse(value))) return false;
  const date = new Date(Date.UTC(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3])));
  return date.getUTCFullYear() === Number(parts[1]) && date.getUTCMonth() + 1 === Number(parts[2]) && date.getUTCDate() === Number(parts[3]);
}

export function normalizeBudgetReference(value, now = Date.now()) {
  if (!value || value.schemaVersion !== 1 || value.currency !== 'USD' || value.unit !== 'TOMAN_PER_USD'
    || !['current', 'retained'].includes(value.connection) || !['fresh', 'stale'].includes(value.sourceFreshness)
    || !Object.entries(BUDGET_REFERENCE_SOURCE).every(([key, expected]) => value.source?.[key] === expected)
    || !validAmount(value.amountToman) || !zonedTime(value.publishedAt) || !zonedTime(value.checkedAt)
    || Date.parse(value.publishedAt) > now + 300000 || Date.parse(value.checkedAt) > now + 300000) return null;
  return {...value, source: {...BUDGET_REFERENCE_SOURCE},
    sourceFreshness: now - Date.parse(value.publishedAt) > 86400000 ? 'stale' : value.sourceFreshness};
}

export function formatBudgetDay(now) {
  return new Intl.DateTimeFormat('fa-IR-u-ca-persian', {timeZone: 'Asia/Tehran', year: 'numeric', month: 'long', day: 'numeric'}).format(new Date(now));
}

function roundedBudget(numerator, denominator, base) {
  const step = BigInt(base >= 1000000000 ? 10000000 : base >= 10000000 ? 1000000 : 100000);
  const value = ((numerator + denominator * step / BigInt(2)) / (denominator * step)) * step;
  const amount = Number(value < step ? step : value);
  return Number.isSafeInteger(amount) && amount <= 900000000000 ? amount : null;
}

export function estimateGoalBudget(goal, reference, quotes, now = Date.now()) {
  const fallback = {amountToman: goal.budgetToman, method: 'baseline', state: 'baseline', publishedAt: BUDGET_BASELINE.publishedAt,
    source: {label: 'پیشنهاد پایهٔ مکعب', url: null}, referenceAmount: null, weightGrams: null};
  if (goal.id === 'custom') return {...fallback, method: 'manual', state: 'manual', publishedAt: null};
  const weighted = metalWeights[goal.id];
  if (weighted) {
    const [metal, weightGrams] = weighted;
    const quote = quotes?.[metal];
    const rate = quote?.sellPriceRial;
    if (!Number.isFinite(rate) || rate <= 0 || rate > Number.MAX_SAFE_INTEGER / 100
      || !quote.publishedAt || !['current', 'retained'].includes(quote.connection)) return fallback;
    const amountToman = roundedBudget(BigInt(Math.round(rate * 100)) * BigInt(weightGrams), BigInt(1000), goal.budgetToman);
    if (amountToman === null) return fallback;
    const timestamp = zonedTime(quote.publishedAt) ? Date.parse(quote.publishedAt) : null;
    if (timestamp !== null && timestamp > now + 300000) return fallback;
    const state = metal === 'copper' ? 'owner' : quote.connection === 'retained' ? 'retained'
      : quote.sourceFreshness === 'stale' || (timestamp !== null && now - timestamp > 86400000) ? 'stale'
        : timestamp === null || quote.sourceFreshness === 'unknown' ? 'unknown' : 'current';
    return {amountToman, method: 'metal', metal, weightGrams, state, publishedAt: quote.publishedAt,
      source: PUBLIC_METAL_SOURCES[metal], referenceAmount: rate / 10};
  }
  const fx = normalizeBudgetReference(reference, now);
  if (!fx) return fallback;
  const amountToman = roundedBudget(BigInt(goal.budgetToman) * BigInt(fx.amountToman), BigInt(BUDGET_BASELINE.usdToman), goal.budgetToman);
  return amountToman === null ? fallback : {amountToman, method: 'fx',
    state: fx.connection === 'retained' ? 'retained' : fx.sourceFreshness === 'stale' ? 'stale' : 'current',
    publishedAt: fx.publishedAt, source: fx.source, referenceAmount: fx.amountToman, weightGrams: null};
}
